import {
  Button,
  ButtonIcon,
  ButtonMenu,
  FileUploadWithModal,
  UploadHistoryTableModal,
  UploadMediaModal,
} from '@/components-v2'
import {MAIN_COLUMNS} from '@/containers/Fulfillment/InventoryList/InventoryListConstants'
import {useProductButtonContainer} from '@/hooks/ProductButtonContainer-hook'
import {useSuggestedSKU} from '@/hooks/useSuggestedSKU.jsx'
import useUploadHistory from '@/hooks/useUploadHistory'
import {getInventoryFileUploadErrors} from '@/services/ProductsService/Fulfillment/inventory'
import {Constants} from '@/utils/constants/commonConstants'
import {PRODUCT_TYPE, UPLOAD_HISTORY_TYPE} from '@/utils/constants/globalConstants'
import {useMemo, useState} from 'react'

const UPLOAD_HISTORY_SUB_COLUMNS = [
  {columnKey: 'productId', header: 'Product ID'},
  {columnKey: 'errorMsg', header: 'STATUS (REMARK)'},
]

// Product is listed first so it's the default selection in the upload modal's type dropdown.
const PRODUCT_ENTITY_TYPE_OPTIONS = [
  {id: PRODUCT_TYPE.PRODUCT, displayText: 'Product'},
  {id: PRODUCT_TYPE.BRAND, displayText: 'Brand'},
  {id: PRODUCT_TYPE.SUBBRAND, displayText: 'Sub Brand'},
  {id: PRODUCT_TYPE.DISPLAYBRAND, displayText: 'Display Brand'},
  {id: PRODUCT_TYPE.CATEGORY, displayText: 'Category'},
  {id: PRODUCT_TYPE.SUBCATEGORY, displayText: 'Sub Category'},
  {id: PRODUCT_TYPE.DISPLAYCATEGORY, displayText: 'Display Category'},
]

/**
 * Custom toolbar header rendered above the v2 Product List DataTable.
 * Owns the upload/download actions menu and its related modals.
 * @param {{
 *   resetFilterButtonOptions?: {onFiltersReset?: () => void, text?: string},
 *   isComparing: boolean,
 *   canCompare: boolean,
 *   onToggleCompare: () => void,
 *   onSettingsClick: () => void,
 *   canDownload: boolean,
 *   isDownloading: boolean,
 *   onDownloadClick: () => void,
 * }} props
 */
export const ProductListTableHeader = ({
  resetFilterButtonOptions,
  isComparing,
  canCompare,
  onToggleCompare,
  onSettingsClick,
  canDownload,
  isDownloading,
  onDownloadClick,
}) => {
  const [openUploadHistory, setOpenUploadHistory] = useState(false)
  // ButtonMenu is a single-select control - remount it after each pick so its internal
  // "selected" state (and the checkmark it renders) doesn't stick to the last clicked action.
  const [actionsMenuKey, setActionsMenuKey] = useState(0)

  const {
    toggleMultipleImagesModal,
    isMultipleImagesModal,
    isProductListUploadModal,
    toggleProductListUploadModal,
    onDownloadProductEnrichmentTemplate,
  } = useProductButtonContainer()
  const {onUploadHistoryPageIndexChange, uploadHistoryData, downloadUploadError} = useUploadHistory(
    UPLOAD_HISTORY_TYPE.PRODUCT,
    openUploadHistory
  )
  const {downloadSuggestedSKUTemplate, setShowSKUModal, showSKUModal} = useSuggestedSKU()

  const uploadHistoryColumns = MAIN_COLUMNS(downloadUploadError)

  const fetchUploadErrors = async parentId => {
    const res = await getInventoryFileUploadErrors(parentId)
    const productRows = res?.rows || res?.data || []

    return productRows.flatMap(row => {
      const errorsArr = row?.errors || []
      const errorCol = Array.isArray(errorsArr)
        ? errorsArr.map(errItem => `${errItem?.errorCol} - ${errItem?.errorMsg}`).join(' , ')
        : ''

      return [{productId: row?.productId ?? '-', errorCol}]
    })
  }

  const actionsMenuItems = useMemo(
    () => [
      {id: 'upload-product-list', label: 'Upload Product List', onClick: () => toggleProductListUploadModal(true)},
      {
        id: 'upload-multiple-images',
        label: 'Upload Multiple Images',
        onClick: () => toggleMultipleImagesModal(true),
      },
      {
        id: 'download-product-enrichment-template',
        label: 'Download Product List Template',
        onClick: onDownloadProductEnrichmentTemplate,
      },
      {id: 'view-upload-history', label: 'View Uploaded History', onClick: () => setOpenUploadHistory(true)},
      {id: 'upload-suggested-skus', label: `Suggested SKU's`, onClick: () => setShowSKUModal(true)},
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onDownloadProductEnrichmentTemplate]
  )

  // ButtonMenu only reports the picked option via onChange - it never calls the option's own onClick.
  const handleActionsMenuChange = value => {
    actionsMenuItems.find(action => action.id === value?.id)?.onClick?.()
    setActionsMenuKey(prevKey => prevKey + 1)
  }

  return (
    <>
      <div className="datatable-custom-header">
        <Button
          variant="tertiary"
          size="medium"
          text={resetFilterButtonOptions?.text ?? 'Reset All'}
          onClick={resetFilterButtonOptions?.onFiltersReset}
        />
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="medium"
            text={isComparing ? 'Show all' : 'Compare'}
            disabled={!isComparing && !canCompare}
            onClick={onToggleCompare}
          />
          <ButtonIcon icon="settings" type="default" label="Table settings" onClick={onSettingsClick} />
          <ButtonIcon
            icon="download"
            type="default"
            label="Download"
            disabled={!canDownload || isDownloading}
            onClick={onDownloadClick}
          />
          <ButtonMenu
            key={actionsMenuKey}
            options={actionsMenuItems}
            onChange={handleActionsMenuChange}
            height={actionsMenuItems.length * 44 + 16}
            width="280px"
            buttonProps={{
              variant: 'tertiary',
              size: 'medium',
              iconLeading: 'more_vert',
              title: 'More actions',
              'aria-label': 'More actions',
            }}
            menuPosition="bottom-left"
          />
        </div>
      </div>
      <UploadHistoryTableModal
        openUploadHistory={openUploadHistory}
        setOpenUploadHistory={setOpenUploadHistory}
        mainData={uploadHistoryData.data}
        totalRecords={uploadHistoryData.totalRecords}
        mainColumns={uploadHistoryColumns}
        subColumns={UPLOAD_HISTORY_SUB_COLUMNS}
        fetchSubRows={fetchUploadErrors}
        isDark={true}
        setPageIndex={onUploadHistoryPageIndexChange}
        data-testid="upload-history-modal"
      />
      <UploadMediaModal
        key={`upload-media-${isMultipleImagesModal}`}
        openUpload={isMultipleImagesModal}
        setOpenUpload={toggleMultipleImagesModal}
        title="Upload Media"
        entityTypeOptions={PRODUCT_ENTITY_TYPE_OPTIONS}
      />
      <FileUploadWithModal
        key={`upload-product-list-${isProductListUploadModal}`}
        openUpload={isProductListUploadModal}
        setOpenUpload={toggleProductListUploadModal}
        title="Upload Product List"
        uploadFor="product"
        uploadKind="template"
        label={'Upload product file'}
        toastMessage={'File has successfully been uploaded and the status can be checked in Upload History'}
        tertiaryButtonText={'Download Template'}
        onClickTertiaryButton={onDownloadProductEnrichmentTemplate}
      />
      <FileUploadWithModal
        key={`upload-suggested-skus-${showSKUModal}`}
        openUpload={showSKUModal}
        setOpenUpload={setShowSKUModal}
        title="Upload Suggested SKU's"
        uploadFor={Constants.uploadSuggestedSKUs}
        uploadKind="suggestedSku"
        label={`Suggested SKU's file (25 MB max).`}
        tertiaryButtonText={'Download Template'}
        onClickTertiaryButton={() => downloadSuggestedSKUTemplate()}
        acceptedExtensions={Constants.CSV}
      />
    </>
  )
}
